import { actualC, algoC, levelRuntimeB, levelTargetRolls, rivalTotal, type Globals, type Level } from "./model.ts";
import { divRound } from "./big.ts";
import { eMilliOf } from "./curve.ts";
import type { RivalDef } from "./fixtures";
import { progressionPace, type PaceConfig, type PaceScenario } from "./progression-pace.ts";

export type CurveMetric = "cost" | "rolls" | "income" | "perRoll" | "E" | "rho" | "dice" | "netDice" | "visits";
export const CURVE_METRICS: CurveMetric[] = ["cost", "rolls", "income", "perRoll", "E", "rho", "dice", "netDice", "visits"];
export const isPaceMetric = (metric: CurveMetric) => metric === "netDice" || metric === "visits";
/** model 2（单掷金币）版本：E 与收入 B 不参与计算，不画；每骰产出直接取每关的 g */
export const curveMetricsFor = (economyModel: 1 | 2 | undefined, withPace = false): CurveMetric[] => {
  const existing = CURVE_METRICS.filter(m => !isPaceMetric(m) && (economyModel !== 2 || (m !== "E" && m !== "income")));
  return withPace ? ["netDice", "visits", ...existing] : existing;
};
export interface EconomySeries { id: string; source: string; metric: CurveMetric; points: Array<{ n: number; value: bigint | null }>; scale: number }
/** 竞品造价只读完整原始格，不用自家公式补齐缺值；不同指标的单位由图表显式处理。 */
export function economySeries(levels: Level[], globals: Globals, rivals: RivalDef[], metrics: CurveMetric[], sources: string[], dice: number | null, pace?: { config: PaceConfig; scenario: PaceScenario }): EconomySeries[] {
  const available = ["formula", "manual", ...rivals.map(r => r.id)];
  const out: EconomySeries[] = [];
  const projection = pace && metrics.some(isPaceMetric) ? progressionPace(levels, globals, pace.config, pace.scenario) : [];
  for (const metric of metrics.filter((m) => curveMetricsFor(globals.economyModel, !!pace).includes(m))) {
    if (isPaceMetric(metric)) {
      out.push({ id: `${metric}:formula`, source: "formula", metric, scale: metric === "netDice" ? 1000 : 1,
        points: projection.map(row => ({ n: row.level, value: metric === "netDice" ? row.netMilli : row.visits })) });
      continue;
    }
    // model 2 的每骰产出就是每关的 g，与造价来源无关
    const independent = ["rolls", "E", "rho", "dice"].includes(metric) || (metric === "perRoll" && globals.economyModel === 2);
    const chosen = independent ? ["formula"] : available.filter(s => sources.includes(s));
    for (const source of chosen) {
      const bs = ["income", "perRoll"].includes(metric) && globals.economyModel !== 2 ? levelRuntimeB(levels, globals, source === "formula" || source === "manual" ? source : `rival:${source}`) : [];
      const scale = metric === "E" ? 1000 : metric === "rho" ? 10000 : 1;
      out.push({ id: `${metric}:${source}`, source, metric, scale, points: levels.map((lv,i) => {
        let value: bigint | null = null;
        if (metric === "cost") value = source === "formula" ? algoC(lv, globals) : source === "manual" ? actualC(lv) : rivalTotal(lv, source);
        else if (metric === "rolls") value = levelTargetRolls(lv,globals);
        else if (metric === "income") value = bs[i];
        else if (metric === "perRoll" && globals.economyModel === 2) value = lv.gMilli ? divRound(BigInt(lv.gMilli), 1000n) : null;
        else if (metric === "perRoll") value = bs[i] != null && globals.E > 0 ? divRound(bs[i]! * eMilliOf(globals.E),1000n) : null;
        else if (metric === "E") value = globals.E > 0 && Number.isFinite(globals.E) ? BigInt(Math.round(globals.E*1000)) : null;
        else if (metric === "rho") value = globals.rho > 0 && Number.isFinite(globals.rho) ? BigInt(Math.round(globals.rho*10000)) : null;
        else value = dice != null && Number.isSafeInteger(dice) && dice > 0 ? BigInt(dice) : null;
        if (["income", "perRoll"].includes(metric) && source !== "formula"
          && (source === "manual" ? actualC(lv) == null : rivalTotal(lv, source) == null)) value = null;
        return { n: lv.id, value };
      }) });
    }
  }
  return out;
}
